import map from 'lodash/map';
import { ColumnsType } from 'rc-table/lib/interface';
import React from 'react';
import { DataTableType, Table, TableCell, TableHeadCell } from '@/components/common/Table';
import { CryptoFrontendStatus, SellCryptoTxsResponse, useBankTxs } from '@/api/queryHooks/useTransactionsController';
import { formatDateAndTime, formatNumberWithCommas, truncateString } from '@/utils/functions';
import { CryptoCurrency, FiatCurrency, PaymentType } from '@/api/common.types';
import { mapTxTypeDtoToTxType } from '@/mappers';
import { useTableHelpers } from '@/components/common/Table/useTableHelpers';
import { RowTxnButton } from '@/views/Transactions/components/TransactionsTable/RowTxnButton';
import { RowStatus } from '@/views/Transactions/components/TransactionsTable/RowStatus';
import { AmountAndCurrency } from '@/views/Transactions/components/TransactionsTable/types';
import { useGetCountries } from '@/api/queryHooks/useKycController';
import { CopyButton } from '@/components/common/buttons';

function getAccount(content: SellCryptoTxsResponse) {
  switch (content.payoutType) {
    case PaymentType.BANK_TRANSFER:
    case PaymentType.NPP_BANK_TRANSFER:
      return content.bankAccountNr;
    case PaymentType.INTERNATIONAL_ABA:
    case PaymentType.INTERNATIONAL_BIC:
    case PaymentType.INTERNATIONAL_SWIFT:
    case PaymentType.INTERNATIONAL_ACC_NO:
    case PaymentType.INTERNATIONAL_CNAPS:
    case PaymentType.INTERNATIONAL_IFSC:
    case PaymentType.INTERNATIONAL_SORT_CODE:
      return content.accountNo;
    case PaymentType.PAYID:
      return content.payId;
    case PaymentType.INTERNATIONAL_IBAN:
      return content.iban;
    default:
      return content.bankAccountNr;
  }
}

function mapBankTransferData(data?: SellCryptoTxsResponse[]): DataTableType[] {
  return map(data, (item) => {
    const { date, time } = formatDateAndTime(item.created);

    return {
      key: item.id ?? Math.random(),
      date: `${time}, ${date}`,
      paymentId: item.id?.split('-')[0],
      account: getAccount(item),
      type: mapTxTypeDtoToTxType(item.payoutType),
      reference: item.billerReference,
      amount: { amount: formatNumberWithCommas(item.fiatAmount ?? 0), currency: item.fiatCurrency },
      crypto: {
        amount: formatNumberWithCommas(item.cryptoAmount ?? 0),
        currency: item.cryptoCurrency,
      },
      country: item.payoutCountryCode,
      status: item.frontendStatus,
      txn: item?.blockChainExplorer
        ? {
            href: `${item?.blockChainExplorer}${item.blockchainTxHash || ''}`,
            txHash: item.blockchainTxHash || '',
          }
        : { href: undefined, txHash: item.blockchainTxHash || '' },
    };
  });
}

export const TransactionsTableTransfer = () => {
  const { sortBy, isDesc, toggleSort, page, setPage, tableData, setTableData } = useTableHelpers();
  const { data: countries } = useGetCountries();
  const { data } = useBankTxs([
    { sortField: sortBy, isDesc, page },
    {
      keepPreviousData: true,
      refetchOnMount: true,
      onSuccess(data) {
        setTableData(mapBankTransferData(data?.content));
      },
    },
  ]);

  const columns: ColumnsType<DataTableType> = [
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('created')} active={sortBy === 'created'} isDesc={isDesc}>
          Date
        </TableHeadCell>
      ),
      align: 'left',
      dataIndex: 'date',
      render: (value: string) => <TableCell>{value}</TableCell>,
      key: 'date',
    },
    {
      title: <TableHeadCell sort={false}>Payment ID</TableHeadCell>,
      align: 'left',
      dataIndex: 'paymentId',
      render: (value: string) => <TableCell isBold>{value}</TableCell>,
      key: 'paymentId',
    },
    {
      title: <TableHeadCell sort={false}>Account</TableHeadCell>,
      align: 'left',
      dataIndex: 'account',
      render: (value: string) => <TableCell isBold>{value}</TableCell>,
      key: 'account',
    },
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('fiatAmount')} active={sortBy === 'fiatAmount'} isDesc={isDesc}>
          Amount
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'amount',
      render: (value: AmountAndCurrency<FiatCurrency>) => (
        <TableCell>
          ${value?.amount} {value?.currency}
        </TableCell>
      ),
      key: 'amount',
    },
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('cryptoAmount')} active={sortBy === 'cryptoAmount'} isDesc={isDesc}>
          Crypto
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'crypto',
      render: (value: AmountAndCurrency<CryptoCurrency>) => (
        <TableCell>
          {value?.amount} {value?.currency}
        </TableCell>
      ),
      key: 'crypto',
    },
    {
      title: (
        <TableHeadCell
          onClick={() => toggleSort('payoutCountryCode')}
          active={sortBy === 'payoutCountryCode'}
          isDesc={isDesc}
        >
          Country
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'country',
      render: (value: string) => <TableCell>{countries?.find((c) => c.iso2 === value)?.name}</TableCell>,
      key: 'country',
    },
    {
      title: (
        <TableHeadCell
          onClick={() => toggleSort('frontendStatus')}
          active={sortBy === 'frontendStatus'}
          isDesc={isDesc}
        >
          Status
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'status',
      render: (value: CryptoFrontendStatus) => (
        <TableCell>
          <RowStatus statusFromBE={value} />
        </TableCell>
      ),
      key: 'status',
    },
    {
      title: <TableHeadCell sort={false}>TXN</TableHeadCell>,
      align: 'right',
      dataIndex: 'txn',
      render: (value: { href: string; txHash?: string }) => (
        <TableCell>
          {value?.href ? (
            <RowTxnButton href={value.href} txHash={value.txHash} />
          ) : (
            <CopyButton iconPosition="left" variants="buyCryptoHash" valueForCopy={value.txHash}>
              {truncateString({
                string: value.txHash,
              })}
            </CopyButton>
          )}
        </TableCell>
      ),
      key: 'txn',
    },
  ];

  return (
    <Table
      data={tableData}
      columns={columns}
      totalElements={data?.totalElements}
      pageSize={data?.size}
      onPaginate={setPage}
    />
  );
};
